import { useState, useCallback, useEffect, useRef, Fragment } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useWebSocket } from '../context/WebSocketContextUtil';
import { useWebSocketSubscription } from '../hooks/useWebSocketSubscription';
import type { StreamHeader } from '@/lib/reliableStream';
import { StreamEnd } from '@/lib/streamEnd';
import { useCredentials } from '../hooks/useCredentials';
import CredentialSelect from './CredentialSelect';
import { credentialTypesFor } from './credentialTypes';

type Mode = 'quick' | 'interactive';

/** The credential already configured for a service's *underlying* connection —
 * NETCONF runs over SSH, eAPI/REST over HTTP — so an enable prompt can default
 * to that instead of asking from scratch. */
function underlyingCredId(
  service: string,
  creds: Record<string, string | null> | undefined,
): string | null {
  if (!creds) return null;
  if (service === 'netconf') return creds.ssh ?? null;
  if (service === 'rest' || service === 'eapi') return creds.http ?? null;
  return creds[service] ?? null;
}

interface ActivationPanelProps {
  deviceId: string;
  organizationId: string;
  /** Region the device lives in — used to create inline credentials in the
   * right scope from the decision-card picker. */
  regionId: string;
  /** "quick" = auto-resolve every decision; "interactive" = ask the operator. */
  mode: Mode;
  /** The credentials the operator picked when adding the device, keyed by service
   * ("snmp" | "ssh" | "http"). A decision for a service defaults its picker to the
   * matching one — the "you picked this" default. */
  deviceCredentials?: Record<string, string | null>;
  onClose: () => void;
}

interface StatusLine {
  step: string;
  /** Human-readable name for the step, declared by the source (gateway or, for
   * the frontend's own steps, set locally). Rendered verbatim. */
  label: string;
  message: string;
  level: string;
}
interface PendingDecision {
  decisionId: string;
  service: string;
  decision: string;
}
interface ServiceOutcome {
  service: string;
  outcome: string;
  detail: string;
}

const asString = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

/** Pull the backend's human-readable message out of an error, not axios's generic
 * "Request failed with status code 4xx". */
function errorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { description?: string; error?: string } | undefined;
    return data?.description ?? data?.error ?? e.message;
  }
  return e instanceof Error ? e.message : fallback;
}

const OUTCOME_TONE: Record<string, string> = {
  created: 'bg-sev-4/15 text-sev-4',
  enabled: 'bg-sev-4/15 text-sev-4',
  added: 'bg-sev-4/15 text-sev-4',
  needs_credential: 'bg-sev-3/15 text-sev-3',
  // The device took the change but the service didn't answer. Not a success and
  // not a no-op — it warrants attention, so it shares the tone of the other
  // needs-a-human outcome rather than `skipped`'s grey.
  configured_unverified: 'bg-sev-3/15 text-sev-3',
  skipped: 'bg-muted text-muted-foreground',
  error: 'bg-sev-1/15 text-sev-1',
};

/** Wire values are snake_case; only this one needs help reading as English. */
const OUTCOME_LABEL: Record<string, string> = {
  configured_unverified: 'configured, not verified',
};

/** Outcomes that leave the operator with something to do. */
const NEEDS_ATTENTION = new Set(['configured_unverified', 'needs_credential', 'error']);

/** Map an engine decision to the operator-facing prompt. `allowDefault` = the
 * decision can proceed with the credential the operator already picked for this
 * device (enable/add); `false` means our credential is known-wrong, so a working
 * one must be supplied (the single-credential `supply_required` case). */
function describeDecision(d: PendingDecision): {
  title: string;
  description: string;
  commitLabel: string;
  allowDefault: boolean;
} {
  const svc = d.service.toUpperCase();
  switch (d.decision) {
    case 'enable_service':
      return {
        title: `Enable ${svc}?`,
        description: `${svc} is off on this device. Enable it using:`,
        commitLabel: 'Enable',
        allowDefault: true,
      };
    case 'add_or_supply':
      return {
        title: `${svc} is on with a credential we don't have`,
        description: `This device accepts additional ${svc} credentials, so we can add one alongside what's already there — nothing existing is removed. Add:`,
        commitLabel: 'Add',
        allowDefault: true,
      };
    case 'supply_required':
    default:
      return {
        title: `${svc} needs a working credential`,
        description: `${svc} is on but our credential doesn't work, and this device accepts only one. Supply a working credential:`,
        commitLabel: 'Use credential',
        allowDefault: false,
      };
  }
}

/** The interactive decision card. One choice — *which credential* — via a single
 * picker, and one button that commits it. The picker defaults to the credential
 * the operator already chose for this device (`proceed`); selecting a different
 * one substitutes it (`retry`). No more "Enable vs Use this" ambiguity: the
 * dropdown *is* the choice, the button just commits whatever's selected. */
function DecisionPrompt({
  pending,
  organizationId,
  regionId,
  deviceCredentialId,
  onAnswer,
}: {
  pending: PendingDecision;
  organizationId: string;
  regionId: string;
  /** The credential the operator picked for this service when adding the device —
   * what `proceed` will use. Null when none was picked (then a pick is required). */
  deviceCredentialId: string | null;
  onAnswer: (choice: string, credentialId?: string) => void;
}) {
  const { data: credentials } = useCredentials(organizationId);
  const { title, description, commitLabel, allowDefault } = describeDecision(pending);

  // Credentials appropriate to the service (SNMP → community/v3, SSH/NETCONF →
  // login pair or key, everything else → login pair).
  const allowedTypes = credentialTypesFor(pending.service);
  const relevant = (credentials ?? []).filter((c) => allowedTypes.includes(c.type));

  // The credential the operator already picked (what "proceed" uses). It's a real
  // default only when we can point at it AND this decision allows proceeding with
  // it (not the "our cred is wrong" supply_required case).
  const pickedCred = deviceCredentialId
    ? relevant.find((c) => c.id === deviceCredentialId)
    : undefined;
  // Usable default = we can name the picked credential AND proceeding with it is
  // valid here (not the "our cred is wrong" supply_required case).
  const defaultCred = allowDefault ? pickedCred : undefined;
  const defaultCredId = defaultCred ? defaultCred.id : undefined;

  // "" = the default (proceed with the device's own credential); any other value
  // is a credential id (retry with it). With no usable default, "" is an
  // unselected placeholder and commit is blocked until a real credential is chosen.
  const [choiceId, setChoiceId] = useState('');
  // Enable/add can always proceed (the enable runs over the config plane; the
  // credential picker only *optionally* names one for the created method).
  // supply_required (allowDefault=false) genuinely needs a working credential.
  // SNMP is the exception: enabling it or adding ours writes a community to
  // the device, so with none picked for this device there is nothing to write,
  // and "proceed" would come back as "needs a credential". Make the pick.
  const needsPick = pending.service.toLowerCase() === 'snmp' && !defaultCred;
  const canCommit = choiceId !== '' || (allowDefault && !needsPick);

  const commit = () => {
    if (choiceId === '') {
      onAnswer('proceed');
    } else {
      onAnswer('retry', choiceId);
    }
  };

  return (
    <div className="rounded-md border p-4 space-y-3 bg-muted/30">
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground mt-1">{description}</p>
      </div>

      <div className="space-y-1">
        <label htmlFor="decision-credential" className="text-xs font-medium text-muted-foreground">
          Credential
        </label>
        <CredentialSelect
          id="decision-credential"
          // The default ("") shows as the credential it stands for.
          value={choiceId !== '' ? choiceId : (defaultCredId ?? null)}
          onChange={(id) => { setChoiceId(id === null || id === defaultCredId ? '' : id); }}
          regionId={regionId}
          types={allowedTypes}
        />
        {defaultCred && choiceId === '' ? (
          <p className="text-xs text-muted-foreground">You picked this one when adding the device.</p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button disabled={!canCommit} onClick={commit}>
          {commitLabel}
        </Button>
        <Button variant="ghost" onClick={() => { onAnswer('not_now'); }}>
          Not now
        </Button>
      </div>
    </div>
  );
}

export default function ActivationPanel({
  deviceId,
  organizationId,
  regionId,
  mode,
  deviceCredentials,
  onClose,
}: ActivationPanelProps) {
  const { addRawListener, sendRaw } = useWebSocket();
  const queryClient = useQueryClient();

  // Activation creates/enables connection methods and changes health — refresh
  // the device's tabs (connections, methods, health, and the device itself) so
  // they don't show stale "not set up" state after the run.
  const invalidateDevice = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['device-connections', deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['connectionMethods', deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['device-health', deviceId] });
    void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
  }, [queryClient, deviceId]);

  // Always refresh on close too — covers closing mid-run or before the result
  // message lands.
  const handleClose = useCallback(() => {
    invalidateDevice();
    onClose();
  }, [invalidateDevice, onClose]);
  // Generate the session id up front so we can subscribe to its stream BEFORE
  // calling realize. The gateway adopts this same id, so nothing is emitted
  // before a subscriber exists — the structural fix for the subscribe-after-
  // realize race (the re-announce loop below is now just belt-and-suspenders
  // for a WS reconnect mid-run).
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID());
  const [running, setRunning] = useState(false);
  const [statusLines, setStatusLines] = useState<StatusLine[]>([]);
  const [pending, setPending] = useState<PendingDecision | null>(null);
  const [outcomes, setOutcomes] = useState<ServiceOutcome[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Watch this run's stream once we have a session (survives a reopen — the
  // gateway session lives on and re-subscribing re-attaches).
  useWebSocketSubscription({ type: 'realization_session', sessionId, deviceId }, true);

  // Decision ids the operator has already answered. The gateway re-announces a
  // still-pending decision (to survive the subscribe-after-realize race), so we
  // must ignore a re-announce of one we've answered — otherwise a late repeat
  // would re-open a card the user already dismissed.
  const answeredRef = useRef<Set<string>>(new Set());

  // Re-run the whole realize. There is no SNMP-only retry endpoint, and realize
  // is the only path that both configures the device and creates the connection
  // method — so retrying the surface is retrying the fix. A new session id
  // re-fires the start effect below and re-subscribes the stream.
  const handleRetry = useCallback(() => {
    setStatusLines([]);
    setPending(null);
    setOutcomes(null);
    setError(null);
    answeredRef.current = new Set();
    setSessionId(crypto.randomUUID());
  }, []);

  // One listener per run. A gateway that sends the run by stream wraps its
  // messages in the run's frames, numbered, so none is lost or reordered across
  // its stream rolls (docs/GatewayReliableTransport.md); this browser is that
  // stream's end. An older gateway sends the same messages bare.
  useEffect(() => {
    const sid = sessionId;
    const handle = (msg: Record<string, unknown>): boolean => {
      if (msg.type === 'provisioning_status' && msg.deviceId === deviceId) {
        const step = asString(msg.step);
        setStatusLines((prev) => [
          ...prev,
          {
            step,
            label: asString(msg.stepLabel) || step,
            message: asString(msg.message),
            level: asString(msg.level, 'info'),
          },
        ]);
        return true;
      }
      if (msg.type === 'provisioning_decision' && msg.sessionId === sid) {
        const decisionId = asString(msg.decisionId);
        // Ignore a re-announced decision we've already answered, and treat a
        // repeat of the current one as a no-op (keep the same object so the card
        // — and any credential the operator is mid-picking — doesn't reset).
        if (answeredRef.current.has(decisionId)) return true;
        setPending((prev) =>
          prev?.decisionId === decisionId
            ? prev
            : {
                decisionId,
                service: asString(msg.service),
                decision: asString(msg.decision),
              },
        );
        return true;
      }
      if (msg.type === 'provisioning_result' && msg.sessionId === sid) {
        setOutcomes(Array.isArray(msg.outcomes) ? (msg.outcomes as ServiceOutcome[]) : []);
        setRunning(false);
        setPending(null);
        invalidateDevice();
        return true;
      }
      return false;
    };
    const stream = new StreamEnd<Record<string, unknown>>({
      sessionId: sid,
      send: (frame) => {
        sendRaw({ ...frame });
      },
      onMessage: (m) => {
        handle(m);
      },
      // The gateway restarted, and the run with it.
      onLost: () => {
        setError('The gateway restarted during activation. Retry to run it again.');
        setRunning(false);
        setPending(null);
      },
      costOf: (m) => (typeof m === 'string' ? m.length : JSON.stringify(m).length),
    });
    const remove = addRawListener((data: unknown) => {
      if (typeof data !== 'object' || data === null) return false;
      const msg = data as Record<string, unknown>;
      if (msg.type === 'stream_frame' && msg.sessionId === sid) {
        if (typeof msg.header === 'object' && msg.header !== null) {
          const inner = typeof msg.message === 'object' && msg.message !== null ? (msg.message as Record<string, unknown>) : null;
          stream.receive(msg.header as StreamHeader, inner);
        }
        return true;
      }
      return handle(msg);
    });
    return () => {
      remove();
      stream.dispose();
    };
  }, [addRawListener, sendRaw, deviceId, sessionId, invalidateDevice]);

  // Auto-run the flow on mount: identify the device (the smart,
  // connection-aware step), then realize its connection surface. Runs once per
  // session id — which is also how Retry works: minting a new id re-fires this.
  //
  // Guarding on the id rather than a bare boolean keeps it StrictMode-safe (a
  // double-invoke sees the same id and no-ops) without pinning it to the first
  // run. Declared *after* `useWebSocketSubscription` above so that on a retry
  // the new session's subscription is registered before this posts — effects
  // run in declaration order, which is the same ordering the client-generated
  // session id exists to guarantee.
  const startedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (startedForRef.current === sessionId) return;
    startedForRef.current = sessionId;

    const run = async () => {
      // The realize session runs identify itself as its first phase, streaming
      // identify status and any credential prompts over the same channel — so we
      // just start the session and render the stream.
      setRunning(true);
      try {
        const resp = await axios.post<{ success: boolean; error?: string; sessionId: string }>(
          `${config.backendApi}/api/devices/${deviceId}/provisioning/realize`,
          { mode, session_id: sessionId },
          { withCredentials: true },
        );
        if (!resp.data.success) {
          setError(resp.data.error || 'Could not start activation');
          setRunning(false);
        }
      } catch (e) {
        setError(errorMessage(e, 'Activation failed to start'));
        setRunning(false);
      }
    };
    void run();
  }, [deviceId, mode, sessionId]);

  const answer = useCallback(
    async (choice: string, credentialId = '') => {
      const current = pending;
      if (!current || !sessionId) return;
      answeredRef.current.add(current.decisionId);
      setPending(null);
      try {
        await axios.post(
          `${config.backendApi}/api/devices/${deviceId}/provisioning/decision`,
          {
            session_id: sessionId,
            decision_id: current.decisionId,
            choice,
            credential_id: credentialId,
          },
          { withCredentials: true },
        );
      } catch (e) {
        setError(errorMessage(e, 'Failed to send answer'));
      }
    },
    [pending, sessionId, deviceId],
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Activate device — {mode === 'quick' ? 'Quick' : 'Guided'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {error && <p className="text-sm text-sev-1">{error}</p>}

          {statusLines.length > 0 && (
            <ScrollArea className="h-48 rounded-md border p-2">
              <div className="grid grid-cols-[minmax(0,auto)_1fr] gap-x-3 gap-y-0.5 text-sm">
                {statusLines.map((l, i) => {
                  // Show the subtask label once per consecutive run so the log
                  // reads as grouped phases rather than a repeated prefix.
                  const showStep = i === 0 || statusLines[i - 1].step !== l.step;
                  const tone =
                    l.level === 'error'
                      ? 'text-sev-1'
                      : l.level === 'warning'
                        ? 'text-sev-3'
                        : 'text-muted-foreground';
                  return (
                    <Fragment key={i}>
                      <span className="whitespace-nowrap text-right text-xs font-medium text-foreground/60">
                        {showStep ? l.label : ''}
                      </span>
                      <span className={tone}>{l.message}</span>
                    </Fragment>
                  );
                })}
              </div>
            </ScrollArea>
          )}

          {pending && (
            <DecisionPrompt
              pending={pending}
              organizationId={organizationId}
              regionId={regionId}
              deviceCredentialId={underlyingCredId(pending.service, deviceCredentials)}
              onAnswer={(choice, credId) => { void answer(choice, credId); }}
            />
          )}

          {running && !pending && (
            <p className="text-sm text-muted-foreground">Realizing…</p>
          )}

          {outcomes && (
            <div className="space-y-2">
              <p className="font-semibold">Activation complete</p>
              <div className="space-y-1">
                {outcomes.map((o) => (
                  // Detail sits on its own line rather than trailing the badge:
                  // `configured_unverified` carries a full explanation of what
                  // was written and what to check, which is unreadable squeezed
                  // onto the end of a row.
                  <div key={o.service} className="space-y-0.5 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{o.service}</Badge>
                      <span className={`rounded px-2 py-0.5 text-xs ${OUTCOME_TONE[o.outcome] ?? 'bg-muted'}`}>
                        {OUTCOME_LABEL[o.outcome] ?? o.outcome}
                      </span>
                    </div>
                    {o.detail && <p className="text-muted-foreground text-xs">{o.detail}</p>}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                {outcomes.some((o) => NEEDS_ATTENTION.has(o.outcome)) && (
                  <Button variant="outline" onClick={handleRetry}>
                    Retry
                  </Button>
                )}
                <Button variant="outline" onClick={handleClose}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
